import type { SelectionState } from "@monolith-forensics/monolith-ui";
import { createElement, useState } from "react";
import { useSnackbar } from "notistack";
import { usePermissions } from "@/hooks";
import { useFeatures } from "@/contexts/FeaturesContext";

import type { GetEvidenceQuery } from "../evidence.api.types";
import type { Evidence } from "../evidence.types";
import { createEvidenceLabelAdapter } from "@/Features/Labels/evidenceLabelAdapter";
import { PrintLabelsMenuInfo } from "@/Features/Labels/components/PrintLabelsMenuInfo";
import type { EvidenceLabelAdapter } from "@/Features/Labels/evidenceLabelAdapter";
import { resolveSelectedEvidence } from "./resolveSelectedEvidence";

/**
 * The "Print Labels" entry for the evidence table's Actions menu, and the
 * PrintLabelsModal props for the selection it was clicked with. Visibility
 * matches single item printing: licensed and not an observer (`hasPermission()`
 * with no argument), and hidden while the `bulk_label_printing` flag is off.
 * Whether the Actions menu shows at all is the table's call, exactly as for
 * the bulk COC items.
 *
 * The selection, the table's query and its loaded rows are snapshotted at the
 * click, so the dialog prints what was selected when it opened. The selection
 * stays after a run.
 */
export const useBulkPrintLabels = ({
  selectionState,
  resolvedQuery,
  orderedRecords,
}: {
  selectionState: SelectionState;
  resolvedQuery: GetEvidenceQuery;
  orderedRecords: Evidence[];
}) => {
  const { hasPermission } = usePermissions();
  const { hasFeatures, FEATURES } = useFeatures();
  const { enqueueSnackbar } = useSnackbar();
  const [adapter, setAdapter] = useState<EvidenceLabelAdapter | null>(null);

  const printMenuItem = {
    label: "Print Labels",
    value: "bulk-print-labels",
    visible: hasPermission() && hasFeatures([FEATURES.BULK_LABEL_PRINTING]),
    rightSection: createElement(PrintLabelsMenuInfo),
    onClick: () => {
      if ((selectionState?.totalSelected || 0) === 0) {
        enqueueSnackbar("Please select at least one evidence item.", {
          variant: "warning",
        });
        return;
      }
      const snapshot = {
        selectionState,
        resolvedQuery,
        loadedRecords: orderedRecords,
      };
      setAdapter(
        createEvidenceLabelAdapter(() => resolveSelectedEvidence(snapshot))
      );
    },
  };

  return {
    printMenuItem,
    /** Set while the dialog is open. */
    adapter,
    close: () => setAdapter(null),
  };
};
